import { get } from "lodash-es";
import React, { FC } from "react";

import { Card, CardContent, Stack } from "@mui/material";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {
  AllSeriesType,
  AxisConfig,
  ChartContainer,
  ChartsTooltip,
  ChartsXAxis,
  ChartsXAxisProps,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts";

import Header from "../../components/Header";
import Page from "../../components/Page";
import { useSelector } from "../../store";
import { useGetTimeSeriesQuery } from "../../store/pomeriumApi";
import { getClusterUsageInterval } from "./interval";
import { useIsHostedCluster } from "@/hooks/use-is-hosted-cluster";

const Usage: FC = () => {
  const { clusterId, organizationId } = useSelector((state) => state.current);

  const now = new Date();
  const dauInterval = getClusterUsageInterval("dau", now);
  const mauInterval = getClusterUsageInterval("mau", now);
  const isHostedCluster = useIsHostedCluster();

  const { data: dau, isLoading: dauLoading } = useGetTimeSeriesQuery(
    {
      organizationId,
      clusterId,
      metricId: "dau",
      startTime: dauInterval.start.toISOString(),
      endTime: dauInterval.end.toISOString(),
    },
    { skip: !organizationId || !clusterId || isHostedCluster },
  );

  const { data: mau, isLoading: mauLoading } = useGetTimeSeriesQuery(
    {
      organizationId,
      clusterId,
      metricId: "mau",
      startTime: mauInterval.start.toISOString(),
      endTime: mauInterval.end.toISOString(),
    },
    { skip: !organizationId || !clusterId || isHostedCluster },
  );

  const noData = !mau?.length && !dau?.length;

  if (!isHostedCluster && (!dau || !mau)) {
    return <LinearProgress />;
  }

  const mauData = mau?.map((ts, i) => {
    return {
      series: {
        id: `mauData_${i}`,
        label: "Monthly Active Users",
        type: "line",
        data: ts.points.map((p) => p.value),
      } as AllSeriesType<"line">,
      xAxis: {
        id: "mauX",
        data: ts.points.map((p) => new Date(p.timestamp)),
        scaleType: "utc",
      } as AxisConfig<"utc", any, ChartsXAxisProps>,
    };
  });

  const dauData = dau?.map((ts, i) => {
    return {
      series: {
        id: `dauData_${i}`,
        label: "Daily Active Users",
        type: "line",
        data: ts.points.map((p) => p.value),
      } as AllSeriesType<"line">,
      xAxis: {
        id: "dauX",
        data: ts.points.map((p) => new Date(p.timestamp)),
        scaleType: "utc",
      } as AxisConfig<"utc", any, ChartsXAxisProps>,
    };
  });

  return (
    <Page title="Usage">
      <Header breadcrumbs={true} title="Usage" />
      <Container maxWidth={false}>
        <Stack>
          {isHostedCluster && (
            <Alert severity="info">
              Usage is not available for hosted clusters.
            </Alert>
          )}
          {noData && !isHostedCluster && (
            <Alert severity="info">
              No data to display. Data is updated hourly.
            </Alert>
          )}
          {!dauLoading && !!dauData?.length && (
            <Card sx={{ mb: 5 }}>
              <CardContent>
                <Typography
                  sx={{ fontSize: 24 }}
                  color="text.primary"
                  gutterBottom
                >
                  Daily Active Users
                </Typography>
                <Stack
                  direction="row"
                  spacing={{ xs: 2, sm: 4, md: 6 }}
                  sx={{ height: "40vh" }}
                >
                  <ChartContainer
                    xAxis={dauData.map((d) => d.xAxis)}
                    series={dauData.map((d) => d.series)}
                  >
                    <LinePlot />
                    <ChartsXAxis axisId={"dauX"} />
                    <ChartsYAxis />
                    <ChartsTooltip trigger="axis" />
                  </ChartContainer>
                  <Stack alignItems="center" sx={{ minWidth: 250 }}>
                    <Typography sx={{ fontSize: 32 }} color="primary">
                      {get(dauData, "0.series.data", [0]).slice(-1)}
                    </Typography>
                    <Typography sx={{ fontSize: 18 }} color="primary">
                      Active Users
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {!mauLoading && !!mauData?.length && (
            <Card>
              <CardContent>
                <Typography
                  sx={{ fontSize: 24 }}
                  color="text.primary"
                  gutterBottom
                >
                  Monthly Active Users
                </Typography>
                <Stack
                  direction="row"
                  spacing={{ xs: 2, sm: 4, md: 6 }}
                  sx={{ height: "40vh" }}
                >
                  <ChartContainer
                    xAxis={mauData.map((d) => d.xAxis)}
                    series={mauData.map((d) => d.series)}
                  >
                    <LinePlot />
                    <ChartsXAxis axisId={"mauX"} />
                    <ChartsYAxis />
                    <ChartsTooltip trigger="axis" />
                  </ChartContainer>
                  <Stack alignItems="center" sx={{ minWidth: 250 }}>
                    <Typography sx={{ fontSize: 32 }} color="primary">
                      {get(mauData, "0.series.data", [0]).slice(-1)}
                    </Typography>
                    <Typography sx={{ fontSize: 18 }} color="primary">
                      Active Users
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </Page>
  );
};

export default Usage;
